import type { components } from "../../../types/voislog_api";
import { AlertKey, reminderDays } from "./notificationSettingItems";

type Schemas = components["schemas"];

/** 1つの設定の編集中の値。API の形のまま持つ */
type SettingDraft<T> = {
  /** 画面に出す値。企業の値に従っているときは、企業の値が入って返る */
  value: T;
  /** 企業の値（default_setting_data） */
  companyValue: T;
  /** 企業の値に従っているか（is_default_used）。保存時に DELETE するか PUT するかを決める */
  followsCompany: boolean;
};

export type NotificationSettingDraft = {
  alert: SettingDraft<Schemas["AlertSettings"]>;
  reminder: SettingDraft<Schemas["ReminderSettings"]>;
  report: SettingDraft<Schemas["ReportSettings"]>;
};

type SettingResponse<T> = {
  setting_data: T;
  default_setting_data: T;
  is_default_used: boolean;
};

function toSettingDraft<T>(response: SettingResponse<T>): SettingDraft<T> {
  return {
    value: response.setting_data,
    companyValue: response.default_setting_data,
    followsCompany: response.is_default_used,
  };
}

// 読み込んだ3つの設定から、編集中の値を作る
export function toDraft(
  alert: Schemas["AlertSettingsResponseWithDefault"],
  reminder: Schemas["ReminderSettingsResponseWithDefault"],
  report: Schemas["ReportSettingsResponseWithDefault"],
): NotificationSettingDraft {
  return {
    alert: toSettingDraft(alert),
    reminder: toSettingDraft(reminder),
    report: toSettingDraft(report),
  };
}

// 自分で値を変えた設定。企業の値に従うのをやめる（元の値に戻しても従わないまま）
function changed<T>(setting: SettingDraft<T>, value: T): SettingDraft<T> {
  return { ...setting, value, followsCompany: false };
}

// 低値・高値・傾向のチェックを押したとき
export function changeAlert(
  draft: NotificationSettingDraft,
  key: AlertKey,
  checked: boolean,
): NotificationSettingDraft {
  const { value } = draft.alert;
  return {
    ...draft,
    alert: changed(draft.alert, {
      ...value,
      mi1: { ...value.mi1, [key]: checked },
    }),
  };
}

// 未測定の選択肢（ラジオの value）を選んだとき。日数なら送る設定にし、以降は7日ごと。"none" なら送らない。
// 現行の管理画面が保存のときに整える値と同じ
export function changeReminder(
  draft: NotificationSettingDraft,
  choice: string,
): NotificationSettingDraft {
  const { value } = draft.reminder;
  const mi1 =
    choice === "none"
      ? {
          enable_send_reminder: false,
          reminder_days_first: null,
          reminder_days_subsequent: null,
        }
      : {
          enable_send_reminder: true,
          reminder_days_first: Number(choice),
          reminder_days_subsequent: 7,
        };
  return {
    ...draft,
    reminder: changed(draft.reminder, {
      ...value,
      mi1: { ...value.mi1, ...mi1 },
    }),
  };
}

// 月間レポートの「通知する」を押したとき
export function changeReport(
  draft: NotificationSettingDraft,
  checked: boolean,
): NotificationSettingDraft {
  const { value } = draft.report;
  return {
    ...draft,
    report: changed(draft.report, { ...value, enable_send_report: checked }),
  };
}

// 企業の値に戻した設定
function followed<T>(setting: SettingDraft<T>): SettingDraft<T> {
  return { ...setting, value: setting.companyValue, followsCompany: true };
}

// 「企業の設定値を使用する」を押したとき。3つとも企業の値に戻す。保存は「保存」を押したときにする
export function followCompany(
  draft: NotificationSettingDraft,
): NotificationSettingDraft {
  return {
    alert: followed(draft.alert),
    reminder: followed(draft.reminder),
    report: followed(draft.report),
  };
}

/**
 * 未測定で選んだ状態にする選択肢（ラジオの value）。送らないなら "none"、
 * 送る設定で日数が選択肢のどれかならその日数。どれにも当たらなければ ""（どれも選ばない）
 */
export function reminderChoice(value: Schemas["ReminderSettings"]): string {
  const { enable_send_reminder, reminder_days_first } = value.mi1;
  if (!enable_send_reminder) return "none";
  return reminderDays.some((days) => days === reminder_days_first)
    ? String(reminder_days_first)
    : "";
}
